/**
 * MentionCollector - Captures mention events for statistical analysis
 *
 * Layer 1: Data Collection
 *
 * Purpose:
 * - Extract all statistical dimensions from mention events
 * - Transform raw mention data into structured stats events
 * - Route events to storage layer
 *
 * Dimensions Captured:
 * - Student (who received the mention)
 * - Teacher (who issued the mention)
 * - Offense (what rule was broken)
 * - Time (when it occurred - hour, period, day of week)
 * - Class (where it occurred)
 * - Severity (how serious)
 *
 * Integration:
 * - Called by MentionService after mention creation
 * - Non-blocking - failures don't affect mention creation
 */

import { BaseCollector } from './BaseCollector.js';

export class MentionCollector extends BaseCollector {
  /**
   * Collect a mention event and extract all statistical dimensions
   * @param {Object} mention - The created mention object
   * @returns {Promise<void>}
   */
  async collect(mention) {
    await this.safeCollect(async () => {
      // Validate required fields
      const requiredFields = ['studentId', 'studentName', 'issuedAt'];
      if (!this.validateEvent(mention, requiredFields)) {
        return;
      }

      const timestamp = this.normalizeTimestamp(mention.issuedAt);

      // Build comprehensive event with all dimensions
      const event = {
        // Event metadata
        type: 'mention',
        timestamp: timestamp,
        dateKey: this.getDateKey(timestamp),
        mentionId: mention.id,

        // Student dimension
        studentId: mention.studentId,
        studentName: mention.studentName,

        // Teacher dimension
        teacherId: mention.issuedById || 'unknown',
        teacherName: mention.issuedByName || 'Unknown',
        teacherRole: mention.issuedByRole || 'teacher',

        // Offense dimension
        offenseId: mention.offenseId || null,
        offenseName: this.normalizeOffenseName(mention),
        offenseCategory: mention.offenseCategory || 'uncategorized',
        offenseCategoryId: mention.offenseCategoryId || null,
        offenseLevel: mention.level || 1, // Use standardized 'level' field
        severity: mention.severity || 'low',

        // Class dimension
        classId: mention.classId || 'unassigned',
        className: mention.className || 'Unassigned',

        // Time dimension
        hour: timestamp.getHours(),
        dayOfWeek: timestamp.getDay(), // 0=Sunday, 6=Saturday
        period: this.getPeriod(timestamp),

        // Mention type dimension
        mentionType: mention.mentionType || 'mention',
        mentionTypeId: mention.mentionTypeId || null,

        // Consequence tracking
        triggeredConsequences: Array.isArray(mention.triggeredConsequences)
          ? mention.triggeredConsequences.length
          : 0,
        triggeredConsequenceIds: mention.triggeredConsequences || []
      };

      this.log('Collecting mention event', {
        studentName: event.studentName,
        offense: event.offenseName,
        period: event.period,
        severity: event.severity
      });

      // Send to storage layer
      await this.storage.recordMention(event);
    });
  }

  /**
   * Normalize offense name from various field names
   * Mentions can have offense, offence, or reason
   * @param {Object} mention - Mention object
   * @returns {string}
   */
  normalizeOffenseName(mention) {
    return mention.offense || mention.offence || mention.reason || 'Unknown';
  }

  /**
   * Determine school period from timestamp
   * Maps hour to school period for pattern detection
   * @param {Date} timestamp - Event timestamp
   * @returns {string}
   */
  getPeriod(timestamp) {
    const hour = timestamp.getHours();
    const minute = timestamp.getMinutes();
    const totalMinutes = hour * 60 + minute;

    // Before school (before 8:00am)
    if (totalMinutes < 480) return 'before-school';

    // Period 1 (8:00-9:00)
    if (totalMinutes < 540) return 'period-1';

    // Period 2 (9:00-10:00)
    if (totalMinutes < 600) return 'period-2';

    // Period 3 (10:00-11:00)
    if (totalMinutes < 660) return 'period-3';

    // Lunch (11:00-12:00)
    if (totalMinutes < 720) return 'lunch';

    // Period 4 (12:00-1:00pm)
    if (totalMinutes < 780) return 'period-4';

    // Period 5 (1:00-2:00pm)
    if (totalMinutes < 840) return 'period-5';

    // Period 6 (2:00-3:00pm)
    if (totalMinutes < 900) return 'period-6';

    // After school (after 3:00pm)
    return 'after-school';
  }

  /**
   * Get period display name for UI
   * @param {string} periodKey - Period key (e.g., 'period-1')
   * @returns {string}
   */
  static getPeriodDisplayName(periodKey) {
    const displayNames = {
      'before-school': 'Before School',
      'period-1': 'Period 1',
      'period-2': 'Period 2',
      'period-3': 'Period 3',
      'lunch': 'Lunch',
      'period-4': 'Period 4',
      'period-5': 'Period 5',
      'period-6': 'Period 6',
      'after-school': 'After School'
    };

    return displayNames[periodKey] || periodKey;
  }

  /**
   * Get day of week name
   * @param {number} dayIndex - Day index (0-6)
   * @returns {string}
   */
  static getDayOfWeekName(dayIndex) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayIndex] || 'Unknown';
  }
}
